/**
 * Serviço unificado para propostas comerciais de consórcio
 * Integra com Supabase via MCP
 */

import { isSupabaseMode } from '@/config/dataSource';
import { PropostaComercialSupabaseService } from './supabase/propostaComercialService';
import {
  PropostaComercial,
  CreatePropostaComercialDTO,
  UpdatePropostaComercialDTO,
  PropostaComercialFilters,
  PropostaPublica
} from '@/features/propostas/types/propostaComercial';
import { PDFService } from './pdfService';

export class UnifiedPropostaComercialService {
  private supabaseService = new PropostaComercialSupabaseService();

  /**
   * Lista propostas comerciais
   */
  async listarPropostas(filtros?: PropostaComercialFilters, tenantId?: string): Promise<PropostaComercial[]> {
    if (isSupabaseMode()) {
      return await this.supabaseService.listarPropostas(filtros, tenantId);
    }

    // TODO: Implementar integração com backend quando necessário
    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Busca uma proposta comercial específica
   */
  async buscarProposta(id: string): Promise<PropostaComercial> {
    if (isSupabaseMode()) {
      return await this.supabaseService.buscarProposta(id);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Busca proposta por UUID (para link público)
   */
  async buscarPropostaPorUuid(uuid: string): Promise<PropostaPublica> {
    if (isSupabaseMode()) {
      return await this.supabaseService.buscarPropostaPorUuid(uuid);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Cria uma nova proposta comercial
   */
  async criarProposta(propostaData: CreatePropostaComercialDTO): Promise<PropostaComercial> {
    if (isSupabaseMode()) {
      return await this.supabaseService.criarProposta(propostaData);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Atualiza uma proposta comercial existente
   */
  async atualizarProposta(id: string, propostaData: UpdatePropostaComercialDTO): Promise<PropostaComercial> {
    if (isSupabaseMode()) {
      return await this.supabaseService.atualizarProposta(id, propostaData);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Exclui uma proposta comercial
   */
  async excluirProposta(id: string): Promise<void> {
    if (isSupabaseMode()) {
      return await this.supabaseService.excluirProposta(id);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Atualiza status da proposta
   */
  async atualizarStatus(id: string, status: PropostaComercial['status']): Promise<PropostaComercial> {
    if (isSupabaseMode()) {
      return await this.supabaseService.atualizarStatus(id, status);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }

  /**
   * Gera PDF da proposta
   */
  async gerarPDF(id: string): Promise<Blob> {
    const proposta = await this.buscarProposta(id);
    return await PDFService.gerarPDFProposta(proposta);
  }

  /**
   * Gera link público da proposta
   */
  async gerarLinkPublico(id: string): Promise<string> {
    const proposta = await this.buscarProposta(id);
    const baseUrl = window.location.origin;
    return `${baseUrl}/propostas/publicas/${proposta.uuid}`;
  }

  /**
   * Registra interesse em uma proposta pública
   */
  async registrarInteresse(uuid: string): Promise<void> {
    if (isSupabaseMode()) {
      return await this.supabaseService.registrarInteresse(uuid);
    }

    throw new Error('Modo backend não implementado para propostas comerciais');
  }
}

// Instância singleton
export const unifiedPropostaComercialService = new UnifiedPropostaComercialService();
